import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  Paper,
  Autocomplete,
  IconButton,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../services/api';

const AddSHEActivity = ({ onBack, initialData }) => {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(!isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [metadata, setMetadata] = useState({
    nextId: '',
    categories: [],
    locations: [],
    departments: []
  });

  const [formData, setFormData] = useState({
    action_id: '',
    date: new Date().toISOString().split('T')[0],
    activity_category: '',
    location: '',
    department: '',
    staff_group: '',
    description: '',
    reference_id: '',
    observations: '',
    recommendations: '',
    priority: 'Medium',
    owner: '',
    quarter: 'Q1',
    status: 'Open',
    evidence: '',
    verification: '',
    comments: ''
  });

  useEffect(() => {
    fetchMetadata();
    if (isEdit) {
        setFormData(initialData);
        setLoading(false);
    }
  }, [initialData, isEdit]);

  const fetchMetadata = async () => {
    try {
      const response = await api.get('/she-metadata');
      const data = response.data;
      setMetadata(data);
      if (!isEdit) {
          setFormData(prev => ({ ...prev, action_id: data.nextId }));
      }
    } catch (error) {
      console.error('Error fetching SHE metadata:', error);
    } finally {
      if (!isEdit) setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.action_id?.trim()) newErrors.action_id = 'Action ID is required';
    if (!formData.activity_category?.trim()) newErrors.activity_category = 'Category is required';
    if (!formData.location?.trim()) newErrors.location = 'Location is required';
    if (!formData.department?.trim()) newErrors.department = 'Department is required';
    if (!formData.description?.trim()) newErrors.description = 'Description is required';
    if (!formData.owner?.trim()) newErrors.owner = 'Action Owner is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    }
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
        setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) {
        setSubmitError('Please fill in all mandatory fields before saving.');
        return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined) {
            data.append(key, formData[key]);
          }
      });
      if (isEdit) {
          data.append('id', initialData.id);
      }
      if (selectedFile) {
          data.append('evidence_file', selectedFile);
      }

      await api.post('/she-events', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onBack(isEdit ? 'SHE record updated successfully' : 'SHE record saved successfully');
    } catch (error) {
      console.error('Error saving SHE activity:', error);
      setSubmitError(error.response?.data?.error || 'System offline. Please check connection.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: '1200px', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => onBack()} sx={{ color: 'white', mr: 2, background: 'rgba(255,255,255,0.05)', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
          {isEdit ? 'Edit Activity' : 'Add Activity'}
        </Typography>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            {submitError}
        </Alert>
      )}

      <Paper className="glass" sx={{ p: 4, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* ID & Date Section */}
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                required
                label="Action ID" 
                name="action_id" 
                value={formData.action_id} 
                onChange={handleInputChange} 
                error={!!errors.action_id}
                helperText={errors.action_id || "Auto-generated format: YYYY-SHE-XXX"}
                sx={{ input: { color: 'white' }, label: { color: '#94a3b8' }, '& .MuiFormHelperText-root': { color: '#64748b' } }} 
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth required label="Date" type="date" name="date" value={formData.date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Quarter" name="quarter" value={formData.quarter} onChange={handleInputChange} sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth select label="Priority" name="priority" value={formData.priority} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white' }, label: { color: '#94a3b8' } }}>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
            </Grid>

            {/* Classification Section */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={metadata.categories}
                value={formData.activity_category}
                onChange={(e, val) => handleAutocompleteChange('activity_category', val)}
                onInputChange={(e, val) => handleAutocompleteChange('activity_category', val)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    required 
                    label="Activity Category" 
                    error={!formData.activity_category && !!errors.activity_category}
                    helperText={errors.activity_category}
                    sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} 
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={metadata.locations}
                value={formData.location}
                onChange={(e, val) => handleAutocompleteChange('location', val)}
                onInputChange={(e, val) => handleAutocompleteChange('location', val)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    required 
                    label="Campus / Location" 
                    error={!formData.location && !!errors.location}
                    helperText={errors.location}
                    sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} 
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                freeSolo
                options={metadata.departments}
                value={formData.department}
                onChange={(e, val) => handleAutocompleteChange('department', val)}
                onInputChange={(e, val) => handleAutocompleteChange('department', val)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    required 
                    label="Department / Unit" 
                    error={!formData.department && !!errors.department}
                    helperText={errors.department}
                    sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} 
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Staff Group" name="staff_group" value={formData.staff_group} onChange={handleInputChange} sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Reference ID" name="reference_id" value={formData.reference_id} onChange={handleInputChange} sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>

            {/* Narrative Section */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                required
                multiline 
                rows={3} 
                label="Description" 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange} 
                error={!!errors.description}
                helperText={errors.description}
                sx={{ textarea: { color: 'white' }, label: { color: '#94a3b8' } }} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Observations" name="observations" value={formData.observations} onChange={handleInputChange} sx={{ textarea: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Recommendations" name="recommendations" value={formData.recommendations} onChange={handleInputChange} sx={{ textarea: { color: 'white' }, label: { color: '#94a3b8' } }} />
            </Grid>

            {/* Ownership & Status */}
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                required
                label="Action Owner" 
                name="owner" 
                value={formData.owner} 
                onChange={handleInputChange} 
                error={!!errors.owner}
                helperText={errors.owner}
                sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Status" name="status" value={formData.status} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white' }, label: { color: '#94a3b8' } }}>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.05)' }} />
                <Typography variant="h6" sx={{ color: '#10b981', mb: 2, fontWeight: 'bold' }}>Resolution & Evidence</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField 
                    fullWidth 
                    label="Verification / Sign Off" 
                    name="verification" 
                    value={formData.verification || ''} 
                    onChange={handleInputChange} 
                    placeholder="Enter personnel name who verified"
                    sx={{ input: { color: 'white' }, label: { color: '#94a3b8' } }} 
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ 
                    p: 2, 
                    border: '1px dashed rgba(255,255,255,0.2)', 
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255,255,255,0.02)'
                }}>
                    <Box>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>Evidence of Completion</Typography>
                        <Typography sx={{ color: (selectedFile || formData.evidence) ? '#10b981' : '#64748b', fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {selectedFile ? selectedFile.name : (formData.evidence ? formData.evidence.split('/').pop() : 'No file chosen')}
                        </Typography>
                    </Box>
                    <input
                        accept="application/pdf,image/*"
                        style={{ display: 'none' }}
                        id="evidence-file-upload"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="evidence-file-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            sx={{ 
                                color: '#10b981', 
                                borderColor: '#10b981', 
                                textTransform: 'none',
                                '&:hover': { borderColor: '#059669', background: 'rgba(16, 185, 129, 0.05)' }
                            }}
                        >
                            {isEdit && formData.evidence ? 'Replace Report' : 'Select Report'}
                        </Button>
                    </label>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <TextField 
                    fullWidth 
                    multiline 
                    rows={2} 
                    label="Comments" 
                    name="comments" 
                    value={formData.comments || ''} 
                    onChange={handleInputChange} 
                    placeholder="Additional closing remarks or context"
                    sx={{ textarea: { color: 'white' }, label: { color: '#94a3b8' } }} 
                />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => onBack()} sx={{ color: '#94a3b8', px: 4 }}>Cancel</Button>
              <Button 
                type="submit"
                variant="contained" 
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                disabled={saving}
                sx={{ 
                  borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  textTransform: 'none',
                  px: 6,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)'
                }}
              >
                {saving ? 'Saving...' : (isEdit ? 'Update Activity' : 'Save Activity Record')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddSHEActivity;
